import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getActiveJob, setActiveJob } from "@/lib/activeJob";
import { SectionTitle, Loading, StatusChip } from "@/components/common";
import { AlertTriangle, CheckCircle2, ShieldCheck, RefreshCw } from "lucide-react";
import { MembershipBilling } from "@/components/MembershipBilling";
import { toast } from "sonner";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between">
    <span style={{ color: "var(--txt2)" }}>{k}</span>
    <span className="font-mono" style={{ color: "var(--txt)" }}>{v}</span>
  </div>
);

export default function Estimator() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const canPrice = true; // Crew001 Manager AND Tech may record the customer's chosen locked-price tier + authorize
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState("");
  const [job, setJob] = useState(null);
  const [tierData, setTierData] = useState(null);   // { services: [...] }
  const [summary, setSummary] = useState(null);     // { services, subtotal, complete, unpriced }
  const [busy, setBusy] = useState("");
  const [resync, setResync] = useState(null);       // { changed, authorized_final_price, live } after a re-sync
  const [reviewPrice, setReviewPrice] = useState({});
  const isMgmt = user?.role === "owner" || user?.role === "manager";

  useEffect(() => {
    api.get("/jobs").then(({ data }) => {
      setJobs(data);
      const ctx = params.get("job");                          // explicit intent from a link
      const saved = getActiveJob(user?.id);                   // this user's own saved active job
      const initial = (ctx && data.find((j) => j.id === ctx)) ? ctx
        : (saved && data.find((j) => j.id === saved)) ? saved
        : "";                                                 // otherwise → main/select-job state
      if (initial) selectJob(initial);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadJob = async (id) => {
    const [j, t, s] = await Promise.all([
      api.get(`/jobs/${id}`),
      api.get(`/jobs/${id}/services/tier-options`),
      api.get(`/jobs/${id}/quote-summary`),
    ]);
    setJob(j.data); setTierData(t.data); setSummary(s.data);
  };

  const selectJob = async (id) => {
    setJobId(id); setJob(null); setTierData(null); setSummary(null); setResync(null);
    if (id) { setActiveJob(user?.id, id); await loadJob(id); }
  };

  const doResync = async () => {
    if (!jobId) return;
    setBusy("resync");
    try {
      const { data } = await api.post(`/jobs/${jobId}/resync-scan`, {});
      await loadJob(jobId);
      setResync(data);
      if (data.changed) toast.warning("Pricing changed since authorization — re-authorize to lock the new total");
      else toast.success(data.has_scan ? "Re-synced from the latest Shadow Scan" : "No Shadow Scan yet — classifications default to Normal");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-sync failed");
    } finally { setBusy(""); }
  };

  const setMembership = async (tier) => {
    if (!job?.customer_id) { toast.error("This job has no customer to attach a membership to"); return; }
    setBusy("membership");
    try {
      await api.post(`/jobs/${jobId}/membership`, { tier });
      await loadJob(jobId);
      toast.success(tier === "none" ? "Membership removed — quote recalculated" : "Membership selected — discount applied. Seller credit finalizes on authorization.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update membership");
    } finally { setBusy(""); }
  };

  const saveReviewPrice = async (sid) => {
    const price = Number(reviewPrice[sid]);
    if (!(price > 0)) { toast.error("Enter a reviewed price greater than 0"); return; }
    const reason = window.prompt("Reason for this reviewed (custom) fence price:");
    if (!reason) return;
    setBusy(sid);
    try {
      await api.patch(`/jobs/${jobId}/services/${sid}`, { manual_price: price, reason });
      await loadJob(jobId);
      toast.success("Reviewed price set");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to set price"); }
    finally { setBusy(""); }
  };

  const selectTier = async (sid, tier) => {
    if (!canPrice) { toast.error("Only a Manager/Owner can select pricing tiers"); return; }
    setBusy(sid + tier);
    try {
      const { data } = await api.post(`/jobs/${jobId}/services/${sid}/select-tier`, { tier });
      toast.success(`Tier set — ${money(data.final_price)}`);
      await loadJob(jobId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Selection failed");
    } finally { setBusy(""); }
  };

  const authorize = async () => {
    setBusy("authorize");
    try {
      const { data } = await api.post(`/jobs/${jobId}/authorize-services`, {});
      toast.success(`Quote authorized — ${money(data.final_price)}. Proceed to payment.`);
      await loadJob(jobId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cannot authorize");
    } finally { setBusy(""); }
  };

  if (jobs.length === 0) return <Loading label="Loading jobs" />;
  const scan = job?.scan || {};
  const hasScan = !!scan.condition;
  const authorized = (job?.quote || {}).multi_service && (job?.quote || {}).final_price;

  return (
    <div className="max-w-6xl">
      <SectionTitle sub="Locked FreshSurf pricing — one tier per service, quote updates instantly">Smart Estimator</SectionTitle>

      <div className="sg-card p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="estimator-job-select">
        <span className="font-head text-sm uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Job</span>
        <select className="sg-input" style={{ maxWidth: 360 }} value={jobId} onChange={(e) => selectJob(e.target.value)} data-testid="est-job">
          <option value="">— Select a job —</option>
          {jobs.map((j) => <option key={j.id} value={j.id}>{j.title || j.id.slice(0, 8)}{j.customer_name ? ` · ${j.customer_name}` : ""}</option>)}
        </select>
        {jobId && (hasScan
          ? <StatusChip status="ready" label={`Scan: ${scan.condition}/${scan.access}/${scan.specialty}`} />
          : <StatusChip status="warning" label="No Shadow Scan — classifications default to Normal" />)}
        {jobId && (
          <button className="sg-btn" style={{ minHeight: 40 }} onClick={doResync} disabled={busy === "resync"} data-testid="resync-scan-btn">
            <RefreshCw size={15} className={busy === "resync" ? "animate-spin" : ""} /> Re-Sync from Shadow Scan
          </button>
        )}
      </div>

      {!jobId ? (
        <div className="sg-card p-8 text-center" data-testid="est-no-job">
          <div className="font-head text-lg font-bold mb-1">Select a job to build a quote</div>
          <p className="text-sm" style={{ color: "var(--txt2)" }}>Choose the job you're pricing above, or open the Estimator from a specific job. Your last active job is remembered for you only.</p>
        </div>
      ) : !tierData ? <Loading label="Loading services" /> : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Per-service tier selection */}
          <div className="lg:col-span-3 flex flex-col gap-4" data-testid="service-tier-list">
            <div className="text-xs" style={{ color: "var(--txt2)" }}>Prices are base per-service tiers. Condition/access/specialty adjustments and the bundle discount are applied once in the Quote Summary.</div>
            {!canPrice && (
              <div className="sg-card p-3 text-sm" data-testid="est-readonly-note" style={{ background: "rgba(59,130,246,0.08)", color: "var(--txt2)" }}>
                View only — pricing tiers and quote authorization are Manager/Owner actions.
              </div>
            )}
            {tierData.services.length === 0 && (
              <div className="sg-card p-6 text-center" data-testid="no-services">
                <div className="font-head text-lg font-bold mb-1">No services on this job</div>
                <p className="text-sm" style={{ color: "var(--txt2)" }}>Add services in the Job Workspace — scope comes from structured services, not the job title.</p>
              </div>
            )}
            {tierData.services.map((svc) => (
              <div key={svc.job_service_id} className="sg-card p-5" data-testid={`svc-${svc.job_service_id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-head text-lg font-bold">{svc.label}</div>
                  {svc.status === "priced"
                    ? <StatusChip status="ready" label={`Selected · ${svc.selected_tier}`} />
                    : <StatusChip status="pending" label="No tier selected" />}
                </div>
                {(svc.category === "fence" || svc.category === "outdoor_furniture") && svc.options.every((o) => !o.price) ? (
                  <div className="p-4 rounded-lg" data-testid={`fence-review-${svc.job_service_id}`} style={{ background: "rgba(245,158,11,0.1)", border: "1px solid var(--warn)" }}>
                    <div className="font-head font-bold" style={{ color: "#F59E0B" }}>Manager / Owner review required</div>
                    <div className="text-xs mt-1 mb-3" style={{ color: "var(--txt2)" }}>{svc.category === "outdoor_furniture" ? "Outdoor Furniture — pricing configuration pending Owner approval. Set a reviewed price (not Outdoor Living area rates)." : "Non-standard fence — solid-fence square-foot pricing is NOT applied. Add photos + measurements, then set a reviewed price."}</div>
                    {isMgmt ? (
                      <div className="flex flex-wrap gap-2 items-end">
                        <div>
                          <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Reviewed price ($)</label>
                          <input className="sg-input mt-1" type="number" min="0" value={reviewPrice[svc.job_service_id] || ""} data-testid={`fence-review-price-${svc.job_service_id}`}
                            onChange={(e) => setReviewPrice((p) => ({ ...p, [svc.job_service_id]: e.target.value }))} placeholder="e.g. 275" />
                        </div>
                        <button className="sg-btn sg-btn-accent" style={{ minHeight: 40 }} disabled={busy === svc.job_service_id} onClick={() => saveReviewPrice(svc.job_service_id)} data-testid={`fence-review-save-${svc.job_service_id}`}>Set Reviewed Price</button>
                      </div>
                    ) : <div className="text-xs" style={{ color: "var(--txt2)" }}>A Manager/Owner will set the reviewed price.</div>}
                  </div>
                ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {svc.options.map((o) => {
                    const selected = svc.selected_tier === o.tier;
                    return (
                      <button key={o.tier} onClick={() => selectTier(svc.job_service_id, o.tier)}
                        disabled={busy === svc.job_service_id + o.tier || !canPrice}
                        data-testid={`svc-tier-${svc.job_service_id}-${o.tier}`}
                        className="sg-card-elev p-4 flex flex-col text-left"
                        style={{ border: selected ? "2px solid var(--accent)" : "1px solid var(--brd)", cursor: "pointer" }}>
                        <div className="flex items-center justify-between">
                          <span className="font-head font-bold uppercase text-sm">{o.label}</span>
                          {selected && <CheckCircle2 size={18} style={{ color: "var(--accent)" }} />}
                        </div>
                        <div className="font-head text-2xl font-extrabold my-2" style={{ color: "var(--accent)" }} data-testid={`svc-price-${svc.job_service_id}-${o.tier}`}>{money(o.price)}</div>
                        <div className="text-xs" style={{ color: "var(--txt2)" }}>{o.included}</div>
                        {o.trainer_validation_required && <div className="text-[10px] mt-1" style={{ color: "#F59E0B" }}>TRAINER VALIDATION REQUIRED</div>}
                        {o.manager_review_required && <div className="text-[10px] mt-1" style={{ color: "#F59E0B" }}>Manager review required</div>}
                      </button>
                    );
                  })}
                </div>
                )}
              </div>
            ))}
          </div>

          {/* Quote summary */}
          <div className="lg:col-span-2">
            <div className="sg-card p-5 sticky top-24" data-testid="quote-summary">
              <div className="font-head text-xl font-bold mb-4">Quote Summary</div>
              {resync?.changed && (
                <div className="mb-3 p-2 rounded-lg text-xs flex items-start gap-2" style={{ background: "rgba(245,158,11,0.12)", color: "#F59E0B" }} data-testid="resync-changed">
                  <RefreshCw size={14} /> <span>Pricing changed since the last authorization (was {money(resync.authorized_final_price)}). Re-authorize to lock the new total.</span>
                </div>
              )}
              {summary && summary.services.length > 0 ? (
                <>
                  <div className="flex flex-col gap-2 mb-3">
                    {summary.services.filter((s) => s.category !== "trash_can_cleaning").map((s) => (
                      <div key={s.job_service_id} className="flex items-start justify-between py-1" data-testid={`summary-row-${s.job_service_id}`}>
                        <div className="pr-2">
                          <div className="text-sm font-semibold" style={{ color: "var(--txt)" }}>{s.display || s.label}</div>
                          <div className="text-xs" style={{ color: "var(--txt2)" }}>
                            {s.tier_label ? s.tier_label : "No tier selected"}{s.detail ? ` · ${s.detail}` : ""}
                          </div>
                        </div>
                        <span className="font-mono text-sm whitespace-nowrap" style={{ color: s.status === "priced" ? "var(--txt)" : "#F59E0B" }}>
                          {s.status === "priced" ? money(s.line_total) : "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t my-2" style={{ borderColor: "var(--brd)" }} />
                  {summary.breakdown ? (
                    <div className="flex flex-col gap-1 text-sm mb-1" data-testid="quote-breakdown">
                      <Row k="Base services subtotal" v={money(summary.breakdown.service_subtotal)} />
                      {summary.breakdown.condition.amount > 0 && <Row k={`Condition adj — ${summary.breakdown.condition.label} (+${Math.round(summary.breakdown.condition.pct * 100)}%)`} v={`+${money(summary.breakdown.condition.amount)}`} />}
                      {summary.breakdown.access.amount > 0 && <Row k={`Access adj — ${summary.breakdown.access.label} (+${Math.round(summary.breakdown.access.pct * 100)}%)`} v={`+${money(summary.breakdown.access.amount)}`} />}
                      {summary.breakdown.specialty.amount > 0 && <Row k={`Specialty adj — ${summary.breakdown.specialty.label}`} v={`+${money(summary.breakdown.specialty.amount)}`} />}
                      {summary.breakdown.trash.amount > 0 && (
                        <div className="py-1" data-testid="quote-trash">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold" style={{ color: "var(--txt)" }}>Trash Can Cleaning ×{summary.breakdown.trash.bins}</span>
                            <span className="font-mono" style={{ color: "var(--txt)" }}>+{money(summary.breakdown.trash.amount)}</span>
                          </div>
                          <div className="text-[11px]" style={{ color: "var(--txt2)" }}>
                            {summary.breakdown.trash.bins} bin{summary.breakdown.trash.bins !== 1 ? "s" : ""}
                            {summary.breakdown.trash.bins >= 2 ? ` · 2-bin base $40${summary.breakdown.trash.bins > 2 ? ` + ${summary.breakdown.trash.bins - 2} × $15` : ""}` : " · flat $25"}
                          </div>
                        </div>
                      )}
                      {summary.breakdown.auto_discount_amount > 0 && (
                        <div className="flex items-center justify-between" data-testid="quote-bundle">
                          <span style={{ color: "#10B981" }}>{summary.breakdown.auto_discount_source === "bundle" ? `Bundle discount (${Math.round(summary.breakdown.bundle_pct * 100)}%)` : `Membership discount (${Math.round(summary.breakdown.membership_pct * 100)}%)`}</span>
                          <span className="font-mono" style={{ color: "#10B981" }}>-{money(summary.breakdown.auto_discount_amount)}</span>
                        </div>
                      )}
                      {summary.breakdown.employee_discount_amount > 0 && (
                        <div className="flex items-center justify-between" data-testid="quote-employee-discount">
                          <span style={{ color: "#10B981" }}>Employee discount ({summary.breakdown.employee_discount_pct}%)</span>
                          <span className="font-mono" style={{ color: "#10B981" }}>-{money(summary.breakdown.employee_discount_amount)}</span>
                        </div>
                      )}
                      {summary.breakdown.manager_review_required && <div className="text-xs mt-1" style={{ color: "#F59E0B" }}>Manager/custom review required for adjustments</div>}
                    </div>
                  ) : null}
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-head text-lg font-bold">TOTAL</span>
                    <span className="font-head text-3xl font-extrabold" style={{ color: "var(--accent)" }} data-testid="quote-subtotal">{money(summary.breakdown ? summary.breakdown.final_price : summary.subtotal)}</span>
                  </div>

                  {canPrice && job?.customer_id && !authorized && (
                    <div className="mt-3 p-3 rounded-lg" data-testid="membership-seller" style={{ background: "var(--bg-elev)" }}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Membership (credited to you on authorization)</div>
                        {summary.membership && summary.membership !== "none" && (
                          <button className="text-xs font-semibold" style={{ color: "#EF4444" }} disabled={busy === "membership"} onClick={() => setMembership("none")} data-testid="remove-membership">Remove</button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {[["none", "No Membership"], ["fresh_care", "Fresh Care — $29/month"], ["fresh_care_plus", "Fresh Care+ — $49/month"], ["fresh_complete", "Fresh Complete — $79/month"]].map(([k, l]) => {
                          const active = (summary.membership || "none") === k;
                          return (
                            <button key={k} className="sg-btn" style={{ minHeight: 36,
                              background: active ? "var(--accent)" : undefined, color: active ? "#fff" : undefined, borderColor: active ? "var(--accent)" : undefined }}
                              disabled={busy === "membership"} onClick={() => setMembership(active ? "none" : k)} data-testid={`sell-membership-${k}`}>
                              {active && k !== "none" ? "✓ " : ""}{l}
                            </button>
                          );
                        })}
                      </div>
                      {(summary.membership && summary.membership !== "none") && (
                        <div className="mt-3 pt-3" style={{ borderTop: "1px solid var(--brd)" }} data-testid="estimator-auto-bill">
                          <div className="text-xs uppercase tracking-wider mb-2" style={{ color: "var(--txt2)" }}>Recurring auto-billing (Square)</div>
                          <MembershipBilling customerId={job.customer_id} jobId={jobId} onChange={() => loadJob(jobId)} />
                        </div>
                      )}
                    </div>
                  )}

                  {!summary.complete ? (
                    <div className="mt-4 p-3 rounded-lg flex items-start gap-2" style={{ background: "rgba(245,158,11,0.1)", color: "#F59E0B" }} data-testid="quote-incomplete">
                      <AlertTriangle size={18} />
                      <div className="text-sm">
                        <b>Quote Incomplete</b> — {summary.unpriced_count} service(s) still need a tier:
                        <div className="mt-1">{summary.unpriced.join(", ")}</div>
                      </div>
                    </div>
                  ) : (
                    <>
                      {authorized
                        ? <div className="mt-4"><StatusChip status="ready" label={`Authorized · ${money(job.quote.final_price)}`} testid="quote-authorized" /></div>
                        : <div className="mt-3 text-xs" style={{ color: "var(--txt2)" }}>All services priced. Authorize to set the job total for payment.</div>}
                      {canPrice && (
                        <button className="sg-btn sg-btn-accent w-full mt-3" onClick={authorize} disabled={busy === "authorize"} data-testid="authorize-quote-btn">
                          <ShieldCheck size={16} /> {authorized ? "Re-Authorize Quote" : "Authorize Quote"}
                        </button>
                      )}
                    </>
                  )}
                </>
              ) : (
                <div className="text-sm" style={{ color: "var(--txt2)" }}>Add services to this job to build a quote.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
